import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { BarChart3, ChevronRight, Table2 } from "lucide-react";
import type { ECharts } from "echarts/core";
import type { EChartsOption } from "./core";
import { donutOption, heatmapOption, seriesOption, sparklineOption, timelineOption, type HeatSpec, type SeriesSpec, type TimelineRow } from "./build";
import { formatterOf } from "./format";
import { useChartTheme, type ChartTheme } from "./theme";
import "./charts.css";

/**
 * 콘솔 공통 차트 부품 (ECharts). 화면은 여기 부품만 쓴다 — 옵션 조립·테마·크기 맞춤·표 보기는 이 파일과 build.ts 가 맡는다.
 * 모든 차트는 "차트 / 표" 를 바꿔 볼 수 있고(접근성·정확한 값), 테마가 바뀌면 다시 그린다.
 */

// ECharts 본체는 차트가 처음 보일 때 따로 받는다 (첫 화면 번들에 넣지 않음). 한 번 받으면 모든 차트가 같이 쓴다
let corePromise: Promise<typeof import("./core")> | null = null;
const loadCore = () => (corePromise ??= import("./core"));

/** ECharts 한 개. 크기는 부모를 따라가고(ResizeObserver), 옵션이 바뀌면 통째로 바꾼다 */
export function EChart({ option, height, label, className, inline = false }: { option: EChartsOption; height: number | string; label: string; className?: string; inline?: boolean }) {
  const el = useRef<HTMLElement>(null);
  const chart = useRef<ECharts | null>(null);
  const latest = useRef(option);
  latest.current = option;
  useEffect(() => {
    const node = el.current!;
    let alive = true;
    let ro: ResizeObserver | null = null;
    void loadCore().then(({ echarts }) => {
      if (!alive) return;
      const c = echarts.init(node, undefined, { renderer: "canvas" });
      chart.current = c;
      c.setOption(latest.current, { notMerge: true });
      ro = new ResizeObserver(() => c.resize());
      ro.observe(node);
    });
    return () => {
      alive = false;
      ro?.disconnect();
      chart.current?.dispose();
      chart.current = null;
    };
  }, []);
  useEffect(() => {
    chart.current?.setOption(option, { notMerge: true, lazyUpdate: true });
  }, [option]);
  // 표 칸·문단 안(작은 추이선)에서는 span 으로 (div 를 넣을 수 없는 자리)
  const Tag = inline ? "span" : "div";
  return <Tag ref={el as never} className={`mc-echart${className ? ` ${className}` : ""}`} style={{ height, display: "block" }} role="img" aria-label={label} />;
}

/** 옵션을 테마와 함께 만든다 (입력·테마가 같으면 다시 만들지 않음) */
function useOption<T>(build: (input: T, t: ChartTheme) => EChartsOption, input: T, deps: unknown[]) {
  const t = useChartTheme();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => build(input, t), [t, ...deps]);
}

/** 제목·설명·차트/표 전환 틀 */
export function ChartPanel({
  title,
  description,
  controls,
  table,
  children,
  initialView = "chart",
}: {
  title: string;
  description?: ReactNode;
  controls?: ReactNode;
  table?: ReactNode;
  children: ReactNode;
  initialView?: "chart" | "table";
}) {
  const [view, setView] = useState(initialView);
  return (
    <section className="mc-chart" aria-label={title}>
      <header>
        <div>
          <h3>{title}</h3>
          {description && <p>{description}</p>}
        </div>
        {table && (
          <div className="mc-chart-view" role="group" aria-label={`${title} 보기`}>
            <button type="button" aria-pressed={view === "chart"} onClick={() => setView("chart")}>
              <BarChart3 size={14} />
              차트
            </button>
            <button type="button" aria-pressed={view === "table"} onClick={() => setView("table")}>
              <Table2 size={14} />표
            </button>
          </div>
        )}
      </header>
      {controls}
      {view === "chart" || !table ? children : table}
    </section>
  );
}

/** 표 보기 (행 = 항목/날짜, 열 = 계열) */
export function ChartTable({ head = "날짜 / 항목", labels, series, format }: { head?: string; labels: string[]; series: { name: string; values: (number | null)[]; previous?: (number | null)[]; missing?: (string | null)[] }[]; format: (n: number | null) => string }) {
  return (
    <div className="mc-chart-table" tabIndex={0} role="region" aria-label="차트 데이터 표">
      <table>
        <thead>
          <tr>
            <th scope="col">{head}</th>
            {series.map((s) => (
              <th scope="col" key={s.name}>
                {s.name}
                {s.previous && " · 이전 기간"}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {labels.map((label, i) => (
            <tr key={`${label}-${i}`}>
              <th scope="row">{label}</th>
              {series.map((s) => (
                <td key={s.name}>
                  {s.values[i] == null ? (s.missing?.[i] ?? format(null)) : format(s.values[i]!)}
                  {s.previous && <small> / {format(s.previous[i] ?? null)}</small>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty({ text = "표시할 데이터가 없습니다." }: { text?: string }) {
  return <p className="mc-chart-empty">{text}</p>;
}

type Frame = { title: string; description?: ReactNode; controls?: ReactNode; initialView?: "chart" | "table"; height?: number; emptyText?: string };

/** 선·영역·막대·누적·비율·가로·순위·이중 축 차트 */
export function SeriesChart(props: SeriesSpec & Frame) {
  const { title, description, controls, initialView, emptyText, labels, series, horizontal } = props;
  const option = useOption(seriesOption, props, [JSON.stringify([labels, series]), props.kind, props.stacked, props.normalized, horizontal, props.rank, props.log, props.max, JSON.stringify(props.markers ?? []), props.format, props.rightFormat, props.showValues]);
  const has = labels.length > 0 && series.some((s) => s.values.some((v) => v != null) || s.previous?.some((v) => v != null));
  const height = props.height ?? (horizontal ? Math.max(160, labels.length * 30 + 60) : 300);
  const f = formatterOf(props.rank ? "rank" : props.format).value;
  return (
    <ChartPanel title={title} description={description} controls={controls} initialView={initialView} table={has ? <ChartTable labels={labels} series={series} format={f} /> : undefined}>
      {has ? <EChart option={option} height={height} label={`${title} 차트`} /> : <Empty text={emptyText} />}
    </ChartPanel>
  );
}

/** 히트맵 (기본: 순위 구간 색) */
export function HeatmapChart(props: HeatSpec & Frame) {
  const { title, description, controls, initialView, emptyText, columns, rows } = props;
  const option = useOption(heatmapOption, props, [JSON.stringify([columns, rows]), props.rank, props.format]);
  const has = rows.some((r) => r.values.some((v) => v != null));
  const height = props.height ?? Math.max(200, rows.length * 32 + 104);
  const f = formatterOf(props.rank === false ? props.format : "rank").value;
  return (
    <ChartPanel title={title} description={description} controls={controls} initialView={initialView} table={has ? <ChartTable head="항목 / 날짜" labels={columns} series={rows} format={f} /> : undefined}>
      {has ? <EChart option={option} height={height} label={`${title} 히트맵`} /> : <Empty text={emptyText} />}
    </ChartPanel>
  );
}

/** 몫 도넛 (우리 n / 전체 m) */
export function ShareDonut({ title, value, total, label, rest, description }: { title: string; value: number; total: number; label?: string; rest?: string; description?: ReactNode }) {
  const option = useOption(donutOption, { value, total, label, rest }, [value, total, label, rest]);
  return (
    <ChartPanel
      title={title}
      description={description}
      table={<ChartTable head="항목" labels={[label ?? "우리 항목", rest ?? "다른 항목"]} series={[{ name: "개수", values: [value, Math.max(0, total - value)] }]} format={formatterOf("count").value} />}
    >
      <EChart option={option} height={180} label={`${title} ${value} / ${total}`} />
    </ChartPanel>
  );
}

/** 표·카드 안 작은 추이선 */
export function Sparkline({ values, rank = false, label = "추이", width = 110, height = 34 }: { values: (number | null)[]; rank?: boolean; label?: string; width?: number; height?: number }) {
  const option = useOption((v: (number | null)[], t) => sparklineOption(v, t, rank), values, [values.join(","), rank]);
  const f = formatterOf(rank ? "rank" : "count").value;
  if (!values.some((v) => v != null)) return <span className="mc-sparkline is-empty" aria-label={`${label} · 기록 없음`}>—</span>;
  return (
    <span className="mc-sparkline" style={{ width, height }}>
      <EChart inline option={option} height={height} label={`${label} · ${values.map(f).join(", ")}`} />
    </span>
  );
}

/** 기기별 연결 구간 */
export function TimelineChart({ title, description, from, to, rows, okLabel, badLabel, extra }: { title: string; description?: ReactNode; from: string; to: string; rows: TimelineRow[]; okLabel?: string; badLabel?: string; extra?: ReactNode }) {
  const option = useOption(timelineOption, { from, to, rows, okLabel, badLabel }, [from, to, JSON.stringify(rows), okLabel, badLabel]);
  return (
    <ChartPanel title={title} description={description} table={extra}>
      {rows.length ? <EChart option={option} height={Math.max(120, rows.length * 40 + 50)} label={`${title} 차트`} /> : <Empty />}
    </ChartPanel>
  );
}

/** 숫자 카드 + 작은 추이 (누르면 그 지표 차트로) */
export function KpiCard({
  label,
  value,
  delta,
  values,
  active,
  onClick,
  onSelect,
  href,
  actionLabel,
  disabled,
}: {
  label: string;
  value: ReactNode;
  delta?: string;
  values: (number | null)[];
  active?: boolean;
  onClick?: () => void;
  onSelect?: () => void;
  href?: string;
  actionLabel?: string;
  disabled?: boolean;
}) {
  const id = useId();
  const handler = onSelect ?? onClick;
  const actionable = !disabled && (!!href || !!handler);
  const name = actionLabel ?? (active === undefined ? `${label} 자세히 보기` : `${label} 추이 보기`);
  const body = (
    <>
      <span className="mc-kpi-label">
        <span>{label}</span>
        {actionable && <ChevronRight size={16} aria-hidden="true" />}
      </span>
      <strong>{value}</strong>
      <small>{delta ?? "이전 기간 비교 꺼짐"}</small>
      <Sparkline values={values} label={`${label} 추이`} />
      {actionable && (
        <span id={id} className="mc-sr">
          {value}
          {delta ? ` · ${delta}` : ""}
        </span>
      )}
    </>
  );
  if (!actionable) return <div className="mc-kpi">{body}</div>;
  if (href)
    return (
      <a className="mc-kpi is-action" href={href} aria-label={name} aria-describedby={id} title={name}>
        {body}
      </a>
    );
  return (
    <button type="button" className="mc-kpi is-action" aria-label={name} aria-describedby={id} title={name} aria-pressed={active} onClick={handler}>
      {body}
    </button>
  );
}

export function KpiGrid({ children }: { children: ReactNode }) {
  return <div className="mc-kpis">{children}</div>;
}
